import { Controller } from '@hotwired/stimulus';
import { FeatureCollection } from 'geojson';
import mapboxgl, { GeoJSONSource, Marker } from 'mapbox-gl';
import type { Map as MapboxMap, MapboxOptions } from 'mapbox-gl';
import type { MapLayerSpecification } from './map_layer_specification';
import pulsingDot from './pulsingDot';

export default class GeojsonMapController extends Controller {
  declare public readonly geoJsonUrlValue: string;
  declare protected readonly configValue: Record<string, unknown>;

  protected map!: MapboxMap;
  protected markers: Marker[] = [];

  public static readonly values = {
    config: Object,
    geoJsonUrl: String
  };

  protected get _mapContainer(): HTMLElement {
    return this.element as HTMLElement;
  }

  protected _mapOptions(): Partial<MapboxOptions> {
    return {};
  }

  protected _mapReadyEvent(): 'load' | 'style.load' {
    return 'style.load';
  }

  protected _shouldConnect(): boolean {
    return Boolean(mapboxgl.accessToken);
  }

  protected _registerPulsingDot(): boolean {
    return true;
  }

  protected _bindProgressUpdateClick(): boolean {
    return true;
  }

  protected _lineLayers(): MapLayerSpecification[] {
    return [];
  }

  protected _handlePointFeatures(_mapData: FeatureCollection): void {
    // Subclasses add HTML markers from point features.
  }

  protected _beforeSetupMap(): void | Promise<void> {
    // Hook for terrain, base image, style recolor, etc.
  }

  protected _afterSetupMap(_mapData: FeatureCollection): void | Promise<void> {
    // Hook for zoomTo, resize, etc.
  }

  public connect(): void {
    if (!this._shouldConnect()) {
      return;
    }

    this.map = new mapboxgl.Map({
      container: this._mapContainer,
      ...this._mapOptions(),
      ...this.configValue
    });

    const event = this._mapReadyEvent();

    this.map.once(event, () => {
      this._runSetup().catch(err => console.error(err));
    });
  }

  public disconnect(): void {
    this._clearMarkers();
    this.map.remove();
  }

  protected _clearMarkers(): void {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
  }

  protected async _runSetup(): Promise<void> {
    await this._beforeSetupMap();
    await this._setupMap();
  }

  public async load(url: string): Promise<FeatureCollection> {
    const data = (await (await fetch(url)).json()) as FeatureCollection;
    const source = this.map.getSource('map-data') as GeoJSONSource | null;

    if (source) {
      source.setData(data);
    } else {
      this.map.addSource('map-data', {
        type: 'geojson',
        data
      });
    }

    return data;
  }

  public getGeoJSONUrlValue(): string {
    return this.geoJsonUrlValue;
  }

  protected async _setupMap(): Promise<void> {
    const mapData = await this.load(this.geoJsonUrlValue);

    if (this._registerPulsingDot()) {
      this.map.addImage('pulsing-dot', pulsingDot(150), { pixelRatio: 2 });
    }

    this._handlePointFeatures(mapData);

    for (const layer of this._lineLayers()) {
      this.map.addLayer(layer);
    }

    if (this._bindProgressUpdateClick()) {
      this.map.on('click', 'progress-updates', e => {
        const [feature] = e.features ?? [];
        const { id } = feature.properties as { id?: string };

        if (id) {
          window.location.hash = `#progress_update_${id}`;
        }
      });
    }

    await this._afterSetupMap(mapData);
  }
}
